"use client"

import React, { useState, useEffect, useRef, useCallback } from "react"
import { Upload, Loader2, ImageOff } from "lucide-react"
import { supabase } from "@/lib/supabase"
import { fetchGenerations, Generation } from "@/lib/api"
import { toast } from "sonner"
import Image from "next/image"
import { cn, getOptimizedImageUrl } from "@/lib/utils"
import { useAuth } from "@/components/auth-provider"
import { useAuthModal } from "@/store/auth-modal-store"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import {
    Dialog,
    DialogContent,
    DialogTrigger,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"

interface MediaSelectorProps {
    onSelect: (url: string) => void
    trigger?: React.ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
    maxFiles?: number
    mode?: 'popover' | 'dialog'
}

export function MediaSelector({ onSelect, trigger, open, onOpenChange, maxFiles, mode }: MediaSelectorProps) {
    const { user: authUser } = useAuth()
    const openAuthModal = useAuthModal((s) => s.open)
    const [isUploading, setIsUploading] = useState(false)
    const [uploads, setUploads] = useState<{ name: string; url: string }[]>([])
    const [generations, setGenerations] = useState<Generation[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [internalOpen, setInternalOpen] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [activeSource, setActiveSource] = useState<'uploads' | 'generations'>('uploads')
    const [brokenUrls, setBrokenUrls] = useState<Set<string>>(new Set())

    const markBroken = useCallback((url: string) => {
        setBrokenUrls((prev) => {
            if (prev.has(url)) return prev
            const next = new Set(prev)
            next.add(url)
            return next
        })
    }, [])

    const isControlled = open !== undefined
    const isOpen = isControlled ? open : internalOpen
    const setIsOpen = isControlled ? onOpenChange! : setInternalOpen

    // Determine mode: explicit prop > trigger presence (popover) > default (dialog)
    const effectiveMode = mode || (trigger ? 'popover' : 'dialog')

    const fetchContent = async () => {
        try {
            setIsLoading(true)
            if (!authUser) return
            const user = authUser

            // Fetch Uploads
            const { data: uploadData } = await supabase
                .storage
                .from('uploads')
                .list(user.id + '/', {
                    limit: 20,
                    offset: 0,
                    sortBy: { column: 'created_at', order: 'desc' },
                })

            if (uploadData) {
                const paths = uploadData.map(file => `${user.id}/${file.name}`)
                const { data: signedUrls } = await supabase
                    .storage
                    .from('uploads')
                    .createSignedUrls(paths, 24 * 3600)

                const files = uploadData.map((file, i) => ({
                    name: file.name,
                    url: signedUrls?.[i]?.signedUrl || `${user.id}/${file.name}`
                }))
                setUploads(files)
            }

            // Fetch Generations
            const response = await fetchGenerations({ 
                limit: 20,
                category: 'image' 
            })
            
            const validImages = response.items.filter(g => 
                g.status === 'COMPLETED' && 
                g.result_url
            )
            setGenerations(validImages)

        } catch (error) {
            console.error('Error fetching content:', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (isOpen) {
            fetchContent()
        }
    }, [isOpen])

    const handleFileUpload = async (file: File) => {
        try {
            setIsUploading(true)

            if (!authUser) {
                setIsOpen(false)
                openAuthModal({ message: "Войдите, чтобы загружать изображения" })
                return
            }
            const user = authUser

            const fileExt = file.name.split('.').pop()
            const fileName = `${Date.now()}.${fileExt}`
            const filePath = `${user.id}/${fileName}`

            const { error: uploadError } = await supabase.storage
                .from('uploads')
                .upload(filePath, file)

            if (uploadError) throw uploadError

            const { data: signedData } = await supabase.storage
                .from('uploads')
                .createSignedUrl(filePath, 24 * 3600)

            handleSelect(signedData?.signedUrl || filePath)
            toast.success("Изображение загружено")
        } catch (error) {
            console.error('Error uploading image:', error)
            toast.error("Ошибка загрузки изображения")
        } finally {
            setIsUploading(false)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) handleFileUpload(file)
    }

    const onDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }, [])

    const onDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }, [])

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
        
        const file = e.dataTransfer.files?.[0]
        if (file && file.type.startsWith('image/')) {
            handleFileUpload(file)
        }
    }, [])

    // Helper to extract URL from potential JSON array string
    const extractUrl = (url: string): string | null => {
        if (!url || typeof url !== 'string') return null
        
        if (url.trim().startsWith('[') && url.trim().endsWith(']')) {
            try {
                const parsed = JSON.parse(url)
                if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
                    return parsed[0]
                }
                return null
            } catch {
                return null
            }
        }
        return url
    }

    // Helper to get optimized image URL — delegates to the shared helper
    // which now handles both /object/public/ and /object/sign/ (signed URL)
    // forms. The previous local implementation only handled public URLs and
    // returned signed URLs untouched, so private-bucket thumbnails fetched
    // the full-resolution original.
    const getOptimizedUrl = (originalUrl: string, width: number = 200) => {
        try {
            const url = extractUrl(originalUrl)
            if (!url) return ""

            // Basic validation
            if (url.startsWith('http')) {
                try {
                    new URL(url)
                } catch {
                    return ""
                }
            }

            return getOptimizedImageUrl(url, width)
        } catch (e) {
            return ""
        }
    }

    const handleSelect = (url: string) => {
        onSelect(url)
        setIsOpen(false)
    }

    const content = (
        <div className="flex flex-col">
            {/* 1. Compact Upload Dropzone (Always visible) */}
            <div 
                className={cn(
                    "relative flex items-center gap-3 p-3 transition-colors cursor-pointer border-b border-border/40",
                    isDragging ? "bg-accent/50" : "hover:bg-muted/50"
                )}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    accept="image/*"
                    onChange={onFileChange}
                    disabled={isUploading}
                />
                
                <div className="w-8 h-8 rounded-md bg-muted/50 flex items-center justify-center shrink-0">
                    {isUploading ? (
                        <Loader2 className="w-4 h-4 text-primary animate-spin" />
                    ) : (
                        <Upload className="w-4 h-4 text-muted-foreground" />
                    )}
                </div>
                
                <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-foreground">
                        {isUploading ? "Загрузка..." : "Загрузить изображение"}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                        Нажмите или перетащите
                    </span>
                </div>
            </div>

            {/* 2. Source Switcher */}
            <div className="px-3 pt-3 pb-2 flex items-center gap-4">
                <button
                    onClick={() => setActiveSource('uploads')}
                    className={cn(
                        "text-xs font-medium transition-colors hover:text-foreground",
                        activeSource === 'uploads' ? "text-foreground" : "text-muted-foreground"
                    )}
                >
                    Недавние
                </button>
                <button
                    onClick={() => setActiveSource('generations')}
                    className={cn(
                        "text-xs font-medium transition-colors hover:text-foreground",
                        activeSource === 'generations' ? "text-foreground" : "text-muted-foreground"
                    )}
                >
                    Генерации
                </button>
            </div>

            {/* 3. Grid Content */}
            <ScrollArea className="h-[320px]">
                <div className="p-3 pt-0">
                    {activeSource === 'uploads' ? (
                        <div className="space-y-3">
                            {uploads.length > 0 ? (
                                <div className="grid grid-cols-3 gap-3">
                                    {uploads.map((file, i) => {
                                        const optUrl = getOptimizedUrl(file.url)
                                        const isBroken = brokenUrls.has(optUrl)
                                        return (
                                            <button
                                                key={i}
                                                className="group relative aspect-square rounded-md overflow-hidden bg-muted transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 opacity-90 hover:opacity-100"
                                                onClick={() => handleSelect(file.url)}
                                            >
                                                {isBroken ? (
                                                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/50">
                                                        <ImageOff className="w-5 h-5" />
                                                    </div>
                                                ) : (
                                                    <Image
                                                        src={optUrl}
                                                        alt={file.name}
                                                        fill
                                                        sizes="120px"
                                                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                                                        onError={() => markBroken(optUrl)}
                                                    />
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            ) : (
                                !isLoading && (
                                    <div className="text-center py-12 text-xs text-muted-foreground">
                                        Нет недавних загрузок
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {generations.length > 0 ? (
                                <div className="grid grid-cols-3 gap-3">
                                    {generations.map((gen) => {
                                        if (!gen.result_url) return null
                                        const rawUrl = extractUrl(gen.result_url)
                                        const optimizedUrl = getOptimizedUrl(gen.result_url)
                                        
                                        if (!rawUrl || !optimizedUrl) return null

                                        const isBroken = brokenUrls.has(optimizedUrl)
                                        return (
                                            <button
                                                key={gen.id}
                                                className="group relative aspect-square rounded-md overflow-hidden bg-muted transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 opacity-90 hover:opacity-100"
                                                onClick={() => handleSelect(rawUrl)}
                                            >
                                                {isBroken ? (
                                                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/50">
                                                        <ImageOff className="w-5 h-5" />
                                                    </div>
                                                ) : (
                                                    <Image
                                                        src={optimizedUrl}
                                                        alt={gen.prompt || "Generated image"}
                                                        fill
                                                        sizes="120px"
                                                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                                                        onError={() => markBroken(optimizedUrl)}
                                                    />
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            ) : (
                                !isLoading && (
                                    <div className="text-center py-12 text-xs text-muted-foreground">
                                        Нет недавних генераций
                                    </div>
                                )
                            )}
                        </div>
                    )}
                    
                    {isLoading && (
                        <div className="grid grid-cols-3 gap-3">
                            {Array.from({ length: 9 }).map((_, i) => (
                                <div key={i} className="aspect-square rounded-md bg-muted/30 animate-pulse border border-white/[0.04]" />
                            ))}
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    )

    if (effectiveMode === 'dialog') {
            return (
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
                    <DialogContent className="sm:max-w-[380px] w-full p-0 overflow-hidden bg-popover/95 backdrop-blur-xl border-border/50 shadow-xl rounded-xl gap-0">
                        <DialogTitle className="sr-only">Выбор медиа</DialogTitle>
                        <DialogDescription className="sr-only">
                            Загрузите изображение или выберите из недавних генераций.
                        </DialogDescription>
                        {content}
                    </DialogContent>
                </Dialog>
            )
        }

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            {trigger && <PopoverTrigger asChild>{trigger}</PopoverTrigger>}
            <PopoverContent 
                className="w-[380px] p-0 overflow-hidden bg-popover border-border/50 shadow-xl rounded-xl" 
                align="start" 
                sideOffset={8}
            >
                {content}
            </PopoverContent>
        </Popover>
    )
}
